import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import {
  getInvoiceStats, getCollections, getOutstanding, getRevenueByDoctor,
  getAppointmentFootfall, getAppointmentsByDoctor, getOccupancySummary, getLengthOfStay,
  getPatientStats,
  type InvoiceStats, type CollectionRow, type OutstandingRow, type DoctorRevenueRow,
  type AppointmentFootfallRow, type DoctorAppointmentRow, type WardOccupancyRow,
  type LengthOfStayStats, type PatientStatsResult,
} from '@/lib/reportsApi';
import { BarChart3, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { format, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, LineChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const todayStr = () => format(new Date(), 'yyyy-MM-dd');
const monthStartStr = () => format(startOfMonth(new Date()), 'yyyy-MM-dd');

const inr = (n: number | null | undefined) => `₹${Number(n ?? 0).toLocaleString('en-IN')}`;
const pct = (num: number, denom: number) => denom > 0 ? `${((num / denom) * 100).toFixed(1)}%` : '—';
const compactNum = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n);
const dateTick = (v: string) => format(new Date(v), 'dd MMM');

const CHART_COLORS = {
  cash: '#3b82f6', upi: '#22c55e', card: '#a855f7', other: '#94a3b8', total: '#0f172a',
  revenue: '#3b82f6', collected: '#22c55e',
  completed: '#22c55e', no_show: '#ef4444',
  occupied: '#ef4444', totalBeds: '#94a3b8',
  newPatients: '#3b82f6', returning: '#22c55e',
};

type Preset = 'today' | 'week' | 'month' | 'last_month' | 'custom';

function getPresetDates(preset: Preset): { from: string; to: string } {
  const today = new Date();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');
  switch (preset) {
    case 'today':
      return { from: fmt(today), to: fmt(today) };
    case 'week':
      return { from: fmt(startOfWeek(today, { weekStartsOn: 1 })), to: fmt(today) };
    case 'month':
      return { from: fmt(startOfMonth(today)), to: fmt(today) };
    case 'last_month': {
      const lm = subMonths(today, 1);
      return { from: fmt(startOfMonth(lm)), to: fmt(new Date(lm.getFullYear(), lm.getMonth() + 1, 0)) };
    }
    default:
      return { from: monthStartStr(), to: fmt(today) };
  }
}

function ChartLoading() {
  return <div className="h-56 rounded-md border bg-muted/20 animate-pulse mb-3" />;
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold mt-1">{value}</div>
    </div>
  );
}

function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="rounded-md border overflow-hidden">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={cn('flex gap-3 px-3 py-2.5 border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} className="h-4 flex-1 rounded bg-muted animate-pulse" />
          ))}
        </div>
      ))}
    </div>
  );
}

const PRESETS: { key: Preset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
];

export default function ReportsPage() {
  const { currentUser } = useAuth();
  const { hasFeature, can } = usePermission();
  const canViewIpd = hasFeature('ipd') && can('ipd:read');
  const canViewSummary = can('reports:summary');

  const [activeTab, setActiveTab] = useState<'financial' | 'patients' | 'appointments' | 'ipd'>('financial');
  const [preset, setPreset] = useState<Preset>('month');
  const [dateFrom, setDateFrom] = useState(monthStartStr());
  const [dateTo, setDateTo] = useState(todayStr());

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const { from, to } = getPresetDates(p);
      setDateFrom(from);
      setDateTo(to);
    }
  };

  const handleDateChange = (field: 'from' | 'to', val: string) => {
    setPreset('custom');
    if (field === 'from') setDateFrom(val);
    else setDateTo(val);
  };

  // Financial
  const [stats, setStats] = useState<InvoiceStats | null>(null);
  const [collections, setCollections] = useState<CollectionRow[]>([]);
  const [outstanding, setOutstanding] = useState<OutstandingRow[]>([]);
  const [byDoctor, setByDoctor] = useState<DoctorRevenueRow[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Patients
  const [patientData, setPatientData] = useState<PatientStatsResult | null>(null);
  const [patientsLoading, setPatientsLoading] = useState(true);

  // Appointments
  const [footfall, setFootfall] = useState<AppointmentFootfallRow[]>([]);
  const [doctorAppts, setDoctorAppts] = useState<DoctorAppointmentRow[]>([]);
  const [apptsLoading, setApptsLoading] = useState(true);

  // IPD
  const [occupancy, setOccupancy] = useState<WardOccupancyRow[]>([]);
  const [los, setLos] = useState<LengthOfStayStats | null>(null);
  const [ipdLoading, setIpdLoading] = useState(true);

  const load = useCallback(() => {
    if (!currentUser?.clinic_id) return;
    setLoading(true);
    Promise.all([
      canViewSummary ? getInvoiceStats(currentUser.clinic_id, { dateFrom, dateTo }) : Promise.resolve(null),
      canViewSummary ? getCollections(currentUser.clinic_id, { dateFrom, dateTo }) : Promise.resolve([]),
      getOutstanding(currentUser.clinic_id),
      canViewSummary ? getRevenueByDoctor(currentUser.clinic_id, { dateFrom, dateTo }) : Promise.resolve([]),
    ])
      .then(([s, c, o, d]) => {
        setStats(s ?? null);
        setCollections(c ?? []);
        setOutstanding(o ?? []);
        setByDoctor(d ?? []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentUser?.clinic_id, dateFrom, dateTo, canViewSummary]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (activeTab !== 'patients' || !currentUser?.clinic_id) return;
    setPatientsLoading(true);
    getPatientStats(currentUser.clinic_id, { dateFrom, dateTo })
      .then(d => setPatientData(d))
      .catch(console.error)
      .finally(() => setPatientsLoading(false));
  }, [activeTab, currentUser?.clinic_id, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab !== 'appointments' || !currentUser?.clinic_id) return;
    setApptsLoading(true);
    Promise.all([
      getAppointmentFootfall(currentUser.clinic_id, { dateFrom, dateTo }),
      getAppointmentsByDoctor(currentUser.clinic_id, { dateFrom, dateTo }),
    ])
      .then(([f, d]) => {
        setFootfall(f ?? []);
        setDoctorAppts(d ?? []);
      })
      .catch(console.error)
      .finally(() => setApptsLoading(false));
  }, [activeTab, currentUser?.clinic_id, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab !== 'ipd' || !canViewIpd || !currentUser?.clinic_id) return;
    setIpdLoading(true);
    getOccupancySummary(currentUser.clinic_id)
      .then(o => setOccupancy(o ?? []))
      .catch(console.error)
      .finally(() => setIpdLoading(false));
  }, [activeTab, canViewIpd, currentUser?.clinic_id]);

  useEffect(() => {
    if (activeTab !== 'ipd' || !canViewIpd || !currentUser?.clinic_id) return;
    getLengthOfStay(currentUser.clinic_id, { dateFrom, dateTo })
      .then(l => setLos(l ?? null))
      .catch(console.error);
  }, [activeTab, canViewIpd, currentUser?.clinic_id, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab === 'ipd' && !canViewIpd) setActiveTab('financial');
  }, [activeTab, canViewIpd]);

  if (!hasFeature('reports')) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <BarChart3 className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">Reports Not Available</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          Reporting is not included in your current plan. Contact support to upgrade.
        </p>
      </div>
    );
  }

  const filteredOutstanding = outstanding.filter(o => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return o.patient_name?.toLowerCase().includes(q) || o.patient_mrn?.toLowerCase().includes(q);
  });

  const collectionTotals = collections.reduce(
    (acc, c) => {
      acc.cash += Number(c.cash); acc.upi += Number(c.upi); acc.card += Number(c.card);
      acc.netbanking += Number(c.netbanking); acc.cheque += Number(c.cheque);
      acc.advance += Number(c.advance); acc.other += Number(c.other); acc.total += Number(c.total);
      return acc;
    },
    { cash: 0, upi: 0, card: 0, netbanking: 0, cheque: 0, advance: 0, other: 0, total: 0 }
  );

  const apptTotals = footfall.reduce(
    (acc, f) => {
      acc.total += Number(f.total); acc.completed += Number(f.completed);
      acc.no_show += Number(f.no_show); acc.cancelled += Number(f.cancelled);
      acc.upcoming += Number(f.upcoming);
      return acc;
    },
    { total: 0, completed: 0, no_show: 0, cancelled: 0, upcoming: 0 }
  );

  const occupancyTotals = occupancy.reduce(
    (acc, w) => {
      acc.total_beds += Number(w.total_beds); acc.occupied_beds += Number(w.occupied_beds);
      return acc;
    },
    { total_beds: 0, occupied_beds: 0 }
  );

  const collectionsChartData = collections.map(c => ({
    date: c.date,
    cash: Number(c.cash), upi: Number(c.upi), card: Number(c.card),
    other: Number(c.netbanking) + Number(c.cheque) + Number(c.advance) + Number(c.other),
    total: Number(c.total),
  }));

  const byDoctorChartData = byDoctor.map(d => ({
    doctor: d.doctor_name ?? 'Unassigned',
    revenue: Number(d.total_revenue), collected: Number(d.collected_amount),
  }));

  const footfallChartData = footfall.map(f => ({
    date: f.date, total: Number(f.total), completed: Number(f.completed), no_show: Number(f.no_show),
  }));

  const doctorApptsChartData = doctorAppts.map(d => ({
    doctor: d.doctor_name ?? 'Unassigned',
    total: Number(d.total_appointments), no_show: Number(d.no_show_count),
  }));

  const occupancyChartData = occupancy.map(w => ({
    ward: w.ward_name, occupied: Number(w.occupied_beds), total: Number(w.total_beds),
  }));

  const patientDailyChartData = (patientData?.daily ?? []).map(r => ({
    date: r.date, new_patients: Number(r.new_patients),
  }));

  const patientByDoctorChartData = (patientData?.byDoctor ?? []).map(r => ({
    doctor: r.doctor_name ?? 'Unassigned',
    new: Number(r.new_count), returning: Number(r.returning_count),
  }));

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex flex-col gap-2 px-4 pt-4 pb-3 border-b shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-muted-foreground" />
            <h1 className="text-lg font-semibold">Reports</h1>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateFrom}
              onChange={e => handleDateChange('from', e.target.value)}
              className="h-8 text-sm"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={e => handleDateChange('to', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        </div>
        {/* Date presets */}
        <div className="flex items-center gap-1.5">
          {PRESETS.map(p => (
            <Button
              key={p.key}
              variant={preset === p.key ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs px-3"
              onClick={() => applyPreset(p.key)}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as typeof activeTab)} className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 pt-3 shrink-0">
          <TabsList>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="patients">Patients</TabsTrigger>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            {canViewIpd && <TabsTrigger value="ipd">IPD</TabsTrigger>}
          </TabsList>
        </div>

        {/* Financial */}
        <TabsContent value="financial" className="flex-1 overflow-auto px-4 py-4 space-y-6 mt-0">
          {canViewSummary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryCard label="Total Revenue" value={inr(stats?.total_revenue)} />
              <SummaryCard label="Collected" value={inr(stats?.collected_amount)} />
              <SummaryCard label="Outstanding" value={inr(stats?.pending_amount)} />
              <SummaryCard label="Total Invoices" value={String(stats?.total_invoices ?? 0)} />
            </div>
          )}

          {canViewSummary && (
            <section>
              <h2 className="text-sm font-medium mb-2">Daily Collections</h2>
              {loading ? <ChartLoading /> : collectionsChartData.length > 0 && (
                <div className="rounded-md border bg-background p-3 h-56 mb-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={collectionsChartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" tickFormatter={dateTick} tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={compactNum} tick={{ fontSize: 11 }} width={40} />
                      <Tooltip labelFormatter={dateTick} formatter={(v: number) => inr(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="cash" name="Cash" stackId="a" fill={CHART_COLORS.cash} />
                      <Bar dataKey="upi" name="UPI" stackId="a" fill={CHART_COLORS.upi} />
                      <Bar dataKey="card" name="Card" stackId="a" fill={CHART_COLORS.card} />
                      <Bar dataKey="other" name="Other" stackId="a" fill={CHART_COLORS.other} radius={[4, 4, 0, 0]} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
              {loading ? <SkeletonTable cols={5} /> : collections.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center rounded-md border">No collections in this period.</div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        {['Date', 'Cash', 'UPI', 'Card', 'Other', 'Total'].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {collections.map((c, i) => (
                        <tr key={c.date} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                          <td className="px-3 py-2.5 whitespace-nowrap">{format(new Date(c.date), 'dd MMM yyyy')}</td>
                          <td className="px-3 py-2.5 text-right">{inr(c.cash)}</td>
                          <td className="px-3 py-2.5 text-right">{inr(c.upi)}</td>
                          <td className="px-3 py-2.5 text-right">{inr(c.card)}</td>
                          <td className="px-3 py-2.5 text-right">{inr(Number(c.netbanking) + Number(c.cheque) + Number(c.advance) + Number(c.other))}</td>
                          <td className="px-3 py-2.5 text-right font-medium">{inr(c.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted/40 font-medium">
                        <td className="px-3 py-2.5">Total</td>
                        <td className="px-3 py-2.5 text-right">{inr(collectionTotals.cash)}</td>
                        <td className="px-3 py-2.5 text-right">{inr(collectionTotals.upi)}</td>
                        <td className="px-3 py-2.5 text-right">{inr(collectionTotals.card)}</td>
                        <td className="px-3 py-2.5 text-right">{inr(collectionTotals.netbanking + collectionTotals.cheque + collectionTotals.advance + collectionTotals.other)}</td>
                        <td className="px-3 py-2.5 text-right">{inr(collectionTotals.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-medium">Outstanding Dues</h2>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search patient, MRN…"
                  className="h-8 pl-8 text-sm w-52"
                />
              </div>
            </div>
            {loading ? <SkeletonTable cols={4} /> : filteredOutstanding.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center rounded-md border">
                {search ? 'No matching patients.' : 'No outstanding dues.'}
              </div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      {['Patient', 'Phone', 'Last Activity', 'Balance Due'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOutstanding.map((o, i) => (
                      <tr key={o.patient_id} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                        <td className="px-3 py-2.5">
                          <div className="font-medium">{o.patient_name}</div>
                          <div className="text-xs text-muted-foreground">{o.patient_mrn}</div>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">{o.phone || '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                          {o.last_transaction_date ? format(new Date(o.last_transaction_date), 'dd MMM yyyy') : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-red-600 font-medium">{inr(o.balance_due)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {canViewSummary && (
            <section>
              <h2 className="text-sm font-medium mb-2">Revenue by Doctor</h2>
              {loading ? <ChartLoading /> : byDoctorChartData.length > 0 && (
                <div className="rounded-md border bg-background p-3 h-56 mb-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byDoctorChartData} margin={{ bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="doctor" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={40} interval={0} />
                      <YAxis tickFormatter={compactNum} tick={{ fontSize: 11 }} width={40} />
                      <Tooltip formatter={(v: number) => inr(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="revenue" name="Total Revenue" fill={CHART_COLORS.revenue} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="collected" name="Collected" fill={CHART_COLORS.collected} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {loading ? <SkeletonTable cols={5} /> : byDoctor.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center rounded-md border">No invoices in this period.</div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        {['Doctor', 'Invoices', 'Total Revenue', 'Collected', 'Balance Due'].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {byDoctor.map((d, i) => (
                        <tr key={d.doctor_id ?? 'unassigned'} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                          <td className="px-3 py-2.5">{d.doctor_name ?? 'Unassigned'}</td>
                          <td className="px-3 py-2.5 text-right">{d.invoice_count}</td>
                          <td className="px-3 py-2.5 text-right">{inr(d.total_revenue)}</td>
                          <td className="px-3 py-2.5 text-right text-green-600">{inr(d.collected_amount)}</td>
                          <td className={cn('px-3 py-2.5 text-right', Number(d.balance_due) > 0 && 'text-red-600 font-medium')}>{inr(d.balance_due)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </TabsContent>

        {/* Patients */}
        <TabsContent value="patients" className="flex-1 overflow-auto px-4 py-4 space-y-6 mt-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard label="Total Registered" value={String(patientData?.summary?.total_patients ?? 0)} />
            <SummaryCard label="New Patients" value={String(patientData?.summary?.new_patients ?? 0)} />
            <SummaryCard label="Returning" value={String(patientData?.summary?.returning_patients ?? 0)} />
            <SummaryCard label="Avg. Visits" value={patientData?.summary?.avg_visits != null ? Number(patientData.summary.avg_visits).toFixed(1) : '—'} />
          </div>

          <section>
            <h2 className="text-sm font-medium mb-2">Daily Registrations</h2>
            {patientsLoading ? <ChartLoading /> : patientDailyChartData.length > 0 && (
              <div className="rounded-md border bg-background p-3 h-56 mb-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={patientDailyChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={dateTick} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                    <Tooltip labelFormatter={dateTick} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="new_patients" name="New Patients" fill={CHART_COLORS.newPatients} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {patientsLoading ? <SkeletonTable cols={2} /> : (patientData?.daily ?? []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center rounded-md border">No registrations in this period.</div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      {['Date', 'New Patients'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(patientData?.daily ?? []).map((r, i) => (
                      <tr key={r.date} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                        <td className="px-3 py-2.5 whitespace-nowrap">{format(new Date(r.date), 'dd MMM yyyy')}</td>
                        <td className="px-3 py-2.5 text-right font-medium">{r.new_patients}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-sm font-medium mb-2">By Doctor</h2>
            {patientsLoading ? <ChartLoading /> : patientByDoctorChartData.length > 0 && (
              <div className="rounded-md border bg-background p-3 h-56 mb-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={patientByDoctorChartData} margin={{ bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="doctor" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={40} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="new" name="New" fill={CHART_COLORS.newPatients} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="returning" name="Returning" fill={CHART_COLORS.returning} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {patientsLoading ? <SkeletonTable cols={4} /> : (patientData?.byDoctor ?? []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center rounded-md border">No patients in this period.</div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      {['Doctor', 'Total', 'New', 'Returning', 'Return Rate'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(patientData?.byDoctor ?? []).map((r, i) => (
                      <tr key={r.doctor_id ?? 'unassigned'} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                        <td className="px-3 py-2.5">{r.doctor_name ?? 'Unassigned'}</td>
                        <td className="px-3 py-2.5 text-right font-medium">{r.patient_count}</td>
                        <td className="px-3 py-2.5 text-right">{r.new_count}</td>
                        <td className="px-3 py-2.5 text-right text-green-600">{r.returning_count}</td>
                        <td className="px-3 py-2.5 text-right">{pct(r.returning_count, r.patient_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </TabsContent>

        {/* Appointments */}
        <TabsContent value="appointments" className="flex-1 overflow-auto px-4 py-4 space-y-6 mt-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard label="Total Appointments" value={String(apptTotals.total)} />
            <SummaryCard label="Completed" value={String(apptTotals.completed)} />
            <SummaryCard label="No-Shows" value={String(apptTotals.no_show)} />
            <SummaryCard label="No-Show Rate" value={pct(apptTotals.no_show, apptTotals.total)} />
          </div>

          <section>
            <h2 className="text-sm font-medium mb-2">Daily Footfall</h2>
            {apptsLoading ? <ChartLoading /> : footfallChartData.length > 0 && (
              <div className="rounded-md border bg-background p-3 h-56 mb-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={footfallChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={dateTick} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                    <Tooltip labelFormatter={dateTick} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="total" name="Total" stroke={CHART_COLORS.total} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="completed" name="Completed" stroke={CHART_COLORS.completed} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="no_show" name="No-Show" stroke={CHART_COLORS.no_show} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {apptsLoading ? <SkeletonTable cols={6} /> : footfall.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center rounded-md border">No appointments in this period.</div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      {['Date', 'Total', 'Completed', 'No-Show', 'Cancelled', 'Upcoming'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {footfall.map((f, i) => (
                      <tr key={f.date} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                        <td className="px-3 py-2.5 whitespace-nowrap">{format(new Date(f.date), 'dd MMM yyyy')}</td>
                        <td className="px-3 py-2.5 text-right font-medium">{f.total}</td>
                        <td className="px-3 py-2.5 text-right text-green-600">{f.completed}</td>
                        <td className="px-3 py-2.5 text-right text-red-600">{f.no_show}</td>
                        <td className="px-3 py-2.5 text-right text-muted-foreground">{f.cancelled}</td>
                        <td className="px-3 py-2.5 text-right text-muted-foreground">{f.upcoming}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-sm font-medium mb-2">By Doctor</h2>
            {apptsLoading ? <ChartLoading /> : doctorApptsChartData.length > 0 && (
              <div className="rounded-md border bg-background p-3 h-56 mb-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={doctorApptsChartData} margin={{ bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="doctor" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={40} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total" name="Total" fill={CHART_COLORS.total} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="no_show" name="No-Shows" fill={CHART_COLORS.no_show} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {apptsLoading ? <SkeletonTable cols={5} /> : doctorAppts.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center rounded-md border">No appointments in this period.</div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      {['Doctor', 'Total', 'Completed', 'No-Shows', 'No-Show Rate'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {doctorAppts.map((d, i) => (
                      <tr key={d.doctor_id ?? 'unassigned'} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                        <td className="px-3 py-2.5">{d.doctor_name ?? 'Unassigned'}</td>
                        <td className="px-3 py-2.5 text-right">{d.total_appointments}</td>
                        <td className="px-3 py-2.5 text-right text-green-600">{d.completed_count}</td>
                        <td className="px-3 py-2.5 text-right text-red-600">{d.no_show_count}</td>
                        <td className="px-3 py-2.5 text-right">{pct(d.no_show_count, d.total_appointments)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </TabsContent>

        {/* IPD */}
        {canViewIpd && (
          <TabsContent value="ipd" className="flex-1 overflow-auto px-4 py-4 space-y-6 mt-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <SummaryCard label="Total Beds" value={String(occupancyTotals.total_beds)} />
              <SummaryCard label="Occupied" value={String(occupancyTotals.occupied_beds)} />
              <SummaryCard label="Available" value={String(occupancyTotals.total_beds - occupancyTotals.occupied_beds)} />
              <SummaryCard label="Occupancy Rate" value={pct(occupancyTotals.occupied_beds, occupancyTotals.total_beds)} />
              <SummaryCard label="Avg. Length of Stay" value={los?.avgLengthOfStay != null ? `${los.avgLengthOfStay.toFixed(1)} days` : '—'} />
              <SummaryCard label="Active Admissions" value={String(los?.activeAdmissions ?? 0)} />
            </div>

            <section>
              <h2 className="text-sm font-medium mb-2">Occupancy by Ward</h2>
              {ipdLoading ? <ChartLoading /> : occupancyChartData.length > 0 && (
                <div className="rounded-md border bg-background p-3 h-56 mb-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={occupancyChartData} margin={{ bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="ward" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={40} interval={0} />
                      <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="total" name="Total Beds" fill={CHART_COLORS.totalBeds} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="occupied" name="Occupied" fill={CHART_COLORS.occupied} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {ipdLoading ? <SkeletonTable cols={4} /> : occupancy.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center rounded-md border">No wards configured.</div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        {['Ward', 'Total Beds', 'Occupied', 'Available', 'Occupancy'].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {occupancy.map((w, i) => (
                        <tr key={w.ward_id} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                          <td className="px-3 py-2.5">{w.ward_name}</td>
                          <td className="px-3 py-2.5 text-right">{w.total_beds}</td>
                          <td className="px-3 py-2.5 text-right text-red-600">{w.occupied_beds}</td>
                          <td className="px-3 py-2.5 text-right text-green-600">{w.total_beds - w.occupied_beds}</td>
                          <td className="px-3 py-2.5 text-right font-medium">{pct(w.occupied_beds, w.total_beds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
